import { useEffect, useState } from "react";
import { Button, Checkbox, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Spinner } from "@fluentui/react-components";
import type { AnalysisResultSection } from "../../../../services/AnalysisResultService";

/** All files of a section, including the ones attached per chip row. */
export function getSectionFiles(section: AnalysisResultSection) {
  return [...section.files, ...section.rows.flatMap(row => row.files ?? [])];
}

interface PrintResultDialogProps {
  open: boolean;
  sections: AnalysisResultSection[];
  onClose: () => void;
  /** Receives the analysis keys of the sections whose files should be rendered into the PDF. */
  onPrint: (includeFilesForSections: string[]) => Promise<void>;
}

export function PrintResultDialog({ open, sections, onClose, onPrint }: PrintResultDialogProps) {
  const sectionsWithFiles = sections.filter(section => getSectionFiles(section).length > 0);
  const [selected, setSelected] = useState<string[]>([]);
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    if (open) setSelected(sections.filter(section => getSectionFiles(section).length > 0).map(section => section.analysisKey));
  }, [open, sections]);

  const allSelected = sectionsWithFiles.length > 0 && selected.length === sectionsWithFiles.length;
  const toggle = (analysisKey: string, checked: boolean) => {
    setSelected(current => checked ? [...current, analysisKey] : current.filter(key => key !== analysisKey));
  };

  const print = async () => {
    setPrinting(true);
    try {
      await onPrint(selected);
    } finally {
      setPrinting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => { if (!data.open && !printing) onClose(); }}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Print analysis result</DialogTitle>
          <DialogContent>
            <p className="mb-3 text-sm">The PDF contains every section with its values. Choose the sections whose attached files should be included.</p>
            {sectionsWithFiles.length === 0 ? (
              <p className="text-sm opacity-80">No section has attached files.</p>
            ) : (
              <div className="grid gap-1">
                <Checkbox
                  label="All sections"
                  checked={allSelected ? true : selected.length > 0 ? "mixed" : false}
                  onChange={(_, data) => setSelected(data.checked ? sectionsWithFiles.map(section => section.analysisKey) : [])}
                />
                {sectionsWithFiles.map(section => (
                  <Checkbox
                    key={section.analysisKey}
                    className="pl-6"
                    label={`${section.title} (${getSectionFiles(section).length} ${getSectionFiles(section).length === 1 ? "file" : "files"})`}
                    checked={selected.includes(section.analysisKey)}
                    onChange={(_, data) => toggle(section.analysisKey, Boolean(data.checked))}
                  />
                ))}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" disabled={printing} onClick={onClose}>Cancel</Button>
            <Button appearance="primary" disabled={printing} icon={printing ? <Spinner size="tiny" /> : undefined} onClick={() => void print()}>
              {printing ? "Generating…" : "Print"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
